import { Component, ElementRef, Input, OnChanges, OnDestroy, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PhotosApiService, PhotoState, PhotoDescriptor, PhotoCrop } from '@core/api/services/photos-api.service';
import { AuthenticatedPhotoDirective } from './authenticated-photo.directive';
import { CommunityPhotoCarouselComponent } from './community-photo-carousel.component';
import { CirclePhotoDraft } from './circle-photo-editor.component';
import { feedbackError } from '@core/api/services/feedback-api.service';
import { DeleteConfirmDialogComponent } from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';

@Component({ selector: 'app-entity-photos', standalone: true, imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatDialogModule, AuthenticatedPhotoDirective, CommunityPhotoCarouselComponent], template: `
  <div class="photos" [class.photos--full]="full" [class.photos--compact-mobile]="compactOnMobile">
    <ng-container *ngIf="!full && !profileEditor">
      <app-community-photo-carousel *ngIf="unit && state()?.photos?.length; else avatar"
        [photos]="state()!.photos" [activeId]="activePhotoId()" [compactOnMobile]="compactOnMobile" (activeIdChange)="selectPhoto($event)" (enlarge)="open()" />
      <ng-template #avatar><div class="photo-avatar" [class.photo-avatar--person]="!unit" [attr.aria-busy]="avatarLoading">
        <button type="button" class="photo-face" (click)="open()"
          [disabled]="busy()" [attr.aria-label]="unit ? 'Ver galería de fotos' : cover ? 'Ver foto de perfil' : state()?.canManage ? 'Agregar foto de perfil' : 'Sin foto de perfil'">
          <span *ngIf="!unit && initials; else photoIcon" class="photo-initials">{{ initials }}</span>
          <ng-template #photoIcon><mat-icon>{{ unit ? 'photo_camera' : 'person' }}</mat-icon></ng-template>
          <img *ngIf="cover as photo" [appAuthenticatedPhoto]="photo.thumbnailUrl" (photoLoadingChange)="imageLoading.set($event)" [alt]="unit ? 'Foto de comunidad' : 'Foto de perfil'" />
        </button>
        <span *ngIf="avatarLoading" class="photo-loading-ring" role="status" aria-label="Cargando foto"></span>
        <ng-container *ngIf="!unit && state()?.canManage">
          <button type="button" class="photo-action photo-action--edit" [disabled]="busy()"
            aria-label="Editar foto de perfil" title="Editar foto" (click)="open()"><mat-icon>edit</mat-icon></button>
        </ng-container>
      </div></ng-template>
    </ng-container>
    <section *ngIf="profileEditor" class="profile-photo-editor">
      <mat-menu #photoMenu="matMenu">
        <button type="button" mat-menu-item [disabled]="busy()" (click)="replaceProfilePhoto()"><mat-icon>upload</mat-icon><span>Cambiar foto</span></button>
        <button type="button" mat-menu-item [disabled]="busy()" (click)="remove(cover!)"><mat-icon>delete_outline</mat-icon><span>Quitar foto</span></button>
      </mat-menu>
      <div *ngIf="cover" class="profile-photo-preview">
        <button type="button" mat-icon-button *ngIf="cover && state()?.canManage" class="profile-photo-options"
          [matMenuTriggerFor]="photoMenu" [disabled]="busy()" aria-label="Opciones de la foto"><mat-icon>more_vert</mat-icon></button>
        <img [appAuthenticatedPhoto]="cover!.mainUrl" alt="Foto de perfil" />
      </div>
      <ng-container *ngIf="state() && !cover" [ngTemplateOutlet]="emptyPhotos"></ng-container>
    </section>
    <ng-container *ngIf="full">
      <ng-container *ngIf="state() && !state()!.photos.length" [ngTemplateOutlet]="emptyPhotos"></ng-container>
      <app-community-photo-carousel *ngIf="state()?.photos?.length" [expanded]="true"
        [photos]="state()!.photos" [activeId]="activePhotoId()" (activeIdChange)="selectPhoto($event)"
        [editable]="state()!.canManage" [saving]="busy()" (framingSave)="saveFraming($event)">
          <div carouselPhotoOptions *ngIf="state()?.canManage && activePhoto as photo">
            <button type="button" mat-icon-button class="gallery-photo-options" [matMenuTriggerFor]="galleryMenu" [disabled]="busy() || framingDirty"
              aria-label="Opciones de la foto" title="Opciones de la foto">
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #galleryMenu="matMenu">
              <button type="button" mat-menu-item [disabled]="photo.isCover" (click)="change(photo)">Usar de portada</button>
              <button type="button" mat-menu-item (click)="replacement = photo; input.click()">Reemplazar foto</button>
              <button type="button" mat-menu-item [disabled]="activePhotoIndex === 0" (click)="move(activePhotoIndex, -1)">Mover antes</button>
              <button type="button" mat-menu-item [disabled]="activePhotoIndex === state()!.photos.length - 1" (click)="move(activePhotoIndex, 1)">Mover después</button>
              <button type="button" mat-menu-item (click)="remove(photo)">Quitar foto</button>
            </mat-menu>
          </div>
      </app-community-photo-carousel>
    </ng-container>
    <ng-template #emptyPhotos>
      <section class="gallery-empty-state">
        <mat-icon class="gallery-empty-icon" aria-hidden="true">add_photo_alternate</mat-icon>
        <p>{{ unit ? 'Todavía no hay fotos de esta comunidad.' : 'Este miembro todavía no tiene foto.' }}</p>
        <button *ngIf="state()?.canManage" type="button" mat-flat-button class="gallery-empty-add" [disabled]="busy()" (click)="addPhoto()">
          <mat-icon>add_photo_alternate</mat-icon>{{ unit ? 'Agregar fotos' : 'Agregar foto' }}
        </button>
      </section>
    </ng-template>
    <input #input type="file" accept="image/jpeg,image/png,image/webp" hidden (change)="selected($event)">
    <p *ngIf="full || busy()" role="status" class="photo-save-status">{{ busy() ? 'Guardando…' : '' }}</p><p *ngIf="error()" role="alert">{{ error() }} <button mat-button type="button" (click)="load()">Recargar</button></p>
    <button *ngIf="pending && error()" mat-button type="button" [disabled]="busy()" (click)="savePending()">Reintentar guardado</button>
  </div>
`, styleUrl: './entity-photos.component.scss' })
export class EntityPhotosComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) id = '';
  @Input() unit = false;
  @Input() full = false;
  @Input() displayName = '';
  @Input() profileEditor = false;
  @Input() initialPhotoId?: string;
  @Input() compactOnMobile = false;
  readonly api = inject(PhotosApiService);
  private readonly dialog = inject(MatDialog);
  readonly state = signal<PhotoState | null>(null);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly metadataLoading = signal(false);
  readonly imageLoading = signal(true);
  get avatarLoading() { return this.metadataLoading() || (!!this.cover && this.imageLoading()); }
  readonly activePhotoId = signal<string | undefined>(undefined);
  readonly carousel = viewChild(CommunityPhotoCarouselComponent);
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('input');
  get framingDirty() { return !!this.carousel()?.editor()?.dirty(); }
  addPhoto() { if (!this.busy() && !this.framingDirty) { this.replacement = undefined; this.fileInput()?.nativeElement.click(); } }
  replaceProfilePhoto() { if (!this.busy()) { this.replacement = this.cover; this.fileInput()?.nativeElement.click(); } }
  replacement?: PhotoDescriptor;
  pending?: { file: File; crop: PhotoCrop; photo?: PhotoDescriptor };
  private generation = 0;
  private framingSaveId?: string;
  private readonly changes = this.api.changed.subscribe(id => { if (id === this.id && !this.busy()) void this.load(); });
  get cover() { return this.state()?.photos.find(p => p.isCover) ?? this.state()?.photos[0]; }
  get activePhoto() { return this.state()?.photos.find(photo => photo.id === this.activePhotoId()) ?? this.cover; }
  get activePhotoIndex() { return this.state()?.photos.findIndex(photo => photo.id === this.activePhoto?.id) ?? -1; }
  selectPhoto(id: string) { this.activePhotoId.set(id); }
  get initials() {
    const parts = this.displayName.trim().split(/\s+/).filter(Boolean);
    return parts.length ? (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() : '';
  }
  ngOnChanges() { this.pending = undefined; this.framingSaveId = undefined; this.state.set(null); this.imageLoading.set(true); this.activePhotoId.set(this.initialPhotoId); void this.load(); }
  ngOnDestroy() { this.changes.unsubscribe(); ++this.generation; }
  async load() {
    const generation = ++this.generation;
    if (!this.id) { this.metadataLoading.set(false); return; }
    this.metadataLoading.set(true);
    try {
      const state = await this.api.state(this.id, this.unit);
      if (generation === this.generation) { this.state.set(state); this.error.set(''); }
    } catch (e) {
      if (generation === this.generation) this.error.set(feedbackError(e));
    } finally {
      if (generation === this.generation) this.metadataLoading.set(false);
    }
  }
  open() {
    if (this.unit) this.dialog.open(PhotoGalleryDialogComponent, { data: { id: this.id, photoId: this.activePhoto?.id }, width: '600px', maxWidth: '94vw', maxHeight: '90vh', autoFocus: 'dialog' });
    else if (this.cover || this.state()?.canManage) this.dialog.open(ProfilePhotoDialogComponent, {
      data: { id: this.id, displayName: this.displayName }, width: '600px', maxWidth: '94vw',
      maxHeight: '90vh', disableClose: false, autoFocus: 'dialog', ariaLabel: 'Foto del miembro'
    });
  }
  async selected(event: Event) {
    const input = event.target as HTMLInputElement, file = input.files?.[0]; input.value = '';
    if (!file || !this.state() || this.busy()) return;
    if (file.size > 10 * 1024 * 1024) { this.error.set('La imagen debe pesar hasta 10 MiB.'); return; }
    const { PhotoCropDialogComponent } = await import('@shared/components/entity-photos/photo-crop-dialog.component');
    const crop = await new Promise<PhotoCrop | undefined>(resolve => this.dialog.open(PhotoCropDialogComponent, { data: { file, unit: this.unit }, width: '680px', maxWidth: '96vw' }).afterClosed().subscribe(resolve));
    if (!crop) return;
    this.framingSaveId = undefined;
    this.pending = { file, crop, photo: this.replacement };
    await this.savePending();
  }
  async savePending() {
    if (!this.pending || !this.state() || this.busy()) return;
    const { file, crop, photo } = this.pending;
    this.busy.set(true); this.error.set('');
    try {
      const state = await this.api.upload(this.id, this.unit, this.state()!, file, crop, photo);
      const savedPhoto = state.photos.find(entry => entry.id === this.framingSaveId);
      if (savedPhoto) this.carousel()?.editor()?.acceptSaved(savedPhoto);
      this.state.set(state); this.pending = undefined; this.framingSaveId = undefined;
    }
    catch (e) { this.error.set(feedbackError(e)); }
    finally { this.busy.set(false); this.replacement = undefined; }
  }
  remove(photo: PhotoDescriptor) {
    if (this.busy()) return;
    this.dialog.open(DeleteConfirmDialogComponent, { data: { title: 'Quitar foto', message: '¿Querés quitar esta foto?', confirmButtonText: 'Quitar', cancelButtonText: 'Cancelar' }, width: '420px' }).afterClosed().subscribe(async confirmed => {
      if (!confirmed || !this.state()) return;
      this.busy.set(true); try { await this.api.remove(this.id, this.unit, this.state()!, photo); await this.load(); } catch (e) { this.error.set(feedbackError(e)); } finally { this.busy.set(false); }
    });
  }
  async change(photo: PhotoDescriptor, ordered = this.state()!.photos) {
    this.busy.set(true); try { this.state.set(await this.api.gallery(this.id, this.state()!, photo.id, ordered)); } catch (e) { this.error.set(feedbackError(e)); } finally { this.busy.set(false); }
  }
  move(index: number, direction: number) {
    const ordered = [...this.state()!.photos]; [ordered[index], ordered[index + direction]] = [ordered[index + direction], ordered[index]];
    void this.change(this.cover!, ordered);
  }
  async saveFraming(draft: CirclePhotoDraft) {
    if (this.busy()) return;
    this.pending = { file: draft.file, crop: draft.crop, photo: draft.photo };
    this.framingSaveId = draft.photo.id;
    await this.savePending();
  }
}
@Component({ standalone: true, imports: [MatDialogModule, MatButtonModule, MatIconModule, EntityPhotosComponent], template: `
  <div class="gallery-dialog-header">
    <h2 mat-dialog-title class="gallery-dialog-title">Foto del miembro</h2>
  </div>
  <mat-dialog-content><app-entity-photos #profile [id]="data.id" [displayName]="data.displayName" [profileEditor]="true" /></mat-dialog-content>
  <mat-dialog-actions align="end"><button type="button" mat-button mat-dialog-close [disabled]="profile.busy()">Cerrar</button></mat-dialog-actions>
`, styleUrl: './entity-photo-dialog.scss' })
export class ProfilePhotoDialogComponent { readonly data = inject<{ id: string; displayName: string }>(MAT_DIALOG_DATA); }
@Component({ standalone: true, imports: [MatDialogModule, MatButtonModule, MatIconModule, EntityPhotosComponent], template: `
  <div class="gallery-dialog-header">
    <h2 mat-dialog-title class="gallery-dialog-title">Fotos de la comunidad</h2>
    @if (gallery.state()?.canManage && gallery.state()?.photos?.length) {
      <button type="button" mat-icon-button class="gallery-add-photo" aria-label="Agregar foto" title="Agregar foto"
        [disabled]="gallery.busy() || gallery.framingDirty" (click)="gallery.addPhoto()"><mat-icon>add_photo_alternate</mat-icon></button>
    }
  </div>
  <mat-dialog-content><app-entity-photos #gallery [id]="data.id" [unit]="true" [full]="true" [initialPhotoId]="data.photoId" /></mat-dialog-content>
  <mat-dialog-actions align="end">
    <button type="button" mat-button mat-dialog-close [disabled]="gallery.busy()">Cerrar</button>
  </mat-dialog-actions>
`, styleUrl: './entity-photo-dialog.scss' })
export class PhotoGalleryDialogComponent { readonly data = inject<{ id: string; photoId?: string }>(MAT_DIALOG_DATA); }
